import React from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Paper,
  Avatar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import WaterDrop from '@mui/icons-material/WaterDrop';
import Agriculture from '@mui/icons-material/Agriculture';
import Business from '@mui/icons-material/Business';
import Science from '@mui/icons-material/Science';
import Construction from '@mui/icons-material/Construction';
import Store from '@mui/icons-material/Store';
import LocalGasStation from '@mui/icons-material/LocalGasStation';
import FilterAlt from '@mui/icons-material/FilterAlt';
import Public from '@mui/icons-material/Public';
import Biotech from '@mui/icons-material/Biotech';
import SupportAgent from '@mui/icons-material/SupportAgent';
import Work from '@mui/icons-material/Work';
import EmojiEmotions from '@mui/icons-material/EmojiEmotions';
import History from '@mui/icons-material/History';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import Settings from '@mui/icons-material/Settings';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
// Temporarily removing Eco icon as it's causing issues
// import Eco from '@mui/icons-material/Eco';
import Recycling from '@mui/icons-material/Recycling';
import { useLanguage } from '../context/LanguageContext';
import CountUp from 'react-countup';

// Define colors for the application
const colors = {
  primary: '#0ea5e9',   // Sky blue
  secondary: '#0d9488',  // Teal
  success: '#10b981',   // Emerald
  warning: '#f59e0b',   // Amber
  info: '#3b82f6',     // Blue
  text: {
    primary: '#1e293b',  // Slate 800
    secondary: '#64748b' // Slate 500
  }
};

// Define the Section component
const Section = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0)
}));

// Define styled components
const StatLabel = styled(Typography)(({ theme }) => ({
  color: 'rgba(255,255,255,0.7)',
  fontSize: '0.75rem',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  position: 'relative',
  display: 'inline-block',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-2px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '20px',
    height: '1px',
    background: 'linear-gradient(90deg, #0ea5e9, #0d9488)',
    opacity: 0.5,
  },
}));

const CategoryCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)'
  }
}));

const CategoryLink = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(1, 3),
  borderRadius: '30px',
  background: colors.primary,
  color: 'white',
  '&:hover': {
    background: colors.secondary
  }
}));

const AdvantageCard = styled(Box)(({ theme, bgcolor }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  background: bgcolor || colors.primary,
  color: 'white',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)'
  }
}));

// Define data arrays
const features = [
  {
    icon: <WaterDrop sx={{ fontSize: 40, color: colors.primary }} />,
    title: "Advanced Water Treatment",
    description: "State-of-the-art water purification systems for industrial and commercial applications"
  },
  {
    icon: <FilterAlt sx={{ fontSize: 40, color: colors.success }} />,
    title: "Wastewater Solutions",
    description: "Efficient wastewater treatment and recycling systems for sustainable operations"
  },
  {
    icon: <Science sx={{ fontSize: 40, color: colors.warning }} />,
    title: "Innovative Technology",
    description: "Cutting-edge water treatment technologies backed by extensive research"
  },
  {
    icon: <SupportAgent sx={{ fontSize: 40, color: colors.secondary }} />,
    title: "24/7 Support",
    description: "Round-the-clock technical support and maintenance services"
  }
];

const mainCategories = [
  {
    icon: <WaterDrop />,
    title: "Water Treatment",
    tagline: "Advanced purification systems"
  },
  {
    icon: <FilterAlt />,
    title: "Wastewater Management",
    tagline: "Efficient recycling solutions"
  },
  {
    icon: <Science />,
    title: "Research & Development",
    tagline: "Innovative technologies"
  },
  {
    icon: <SupportAgent />,
    title: "Technical Support",
    tagline: "24/7 expert assistance"
  }
];

const industries = [
  {
    name: "Textile",
    icon: <Store />
  },
  {
    name: "Pharmaceutical",
    icon: <Science />
  },
  {
    name: "Food Processing",
    icon: <Agriculture />
  },
  {
    name: "Construction",
    icon: <Construction />
  },
  {
    name: "Oil & Gas",
    icon: <LocalGasStation />
  },
  {
    name: "Manufacturing",
    icon: <Business />
  }
];

const stats = [
  { 
    value: 500, 
    suffix: "+", 
    label: "Projects",
    icon: <Work sx={{ fontSize: 20, color: 'rgba(255,255,255,0.8)' }} />,
    gradient: 'linear-gradient(135deg, #0ea5e9, #0d9488)'
  },
  { 
    value: 98, 
    suffix: "%", 
    label: "Satisfaction",
    icon: <EmojiEmotions sx={{ fontSize: 20, color: 'rgba(255,255,255,0.8)' }} />,
    gradient: 'linear-gradient(135deg, #4ade80, #22c55e)'
  },
  { 
    value: 20, 
    suffix: "+", 
    label: "Years",
    icon: <History sx={{ fontSize: 20, color: 'rgba(255,255,255,0.8)' }} />,
    gradient: 'linear-gradient(135deg, #f472b6, #ec4899)'
  },
  { 
    value: 15, 
    suffix: "+", 
    label: "Countries",
    icon: <Public sx={{ fontSize: 20, color: 'rgba(255,255,255,0.8)' }} />,
    gradient: 'linear-gradient(135deg, #fbbf24, #f59e0b)'
  }
];

export default function Home() {
  const { language } = useLanguage();
  const [activeSlide, setActiveSlide] = React.useState(0);
  const testimonialRef = React.useRef(null);
  
  // Slideshow images
  const slideshowImages = [
    '/images/Slide/sl-1.jpg',
    '/images/Slide/sl-2.jpg',
    '/images/Slide/sl-3.jpg',
  ];
  
  // Video playlist for shuffling
  const videoPlaylist = [
    '/video/promo.mp4',
    // Add more video paths here when available
  ];
  
  // State for current video and mute status
  const [currentVideoIndex, setCurrentVideoIndex] = React.useState(0);
  const [isMuted, setIsMuted] = React.useState(true); // Start muted to ensure autoplay
  const videoRef = React.useRef(null);

  // Auto-advance slideshow
  React.useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slideshowImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slideshowImages.length]);

  // Auto-scroll testimonials
  React.useEffect(() => {
    const scrollInterval = setInterval(() => {
      if (testimonialRef.current) {
        testimonialRef.current.scrollLeft += 400;
        
        // Reset scroll position when reaching the end
        const scrollWidth = testimonialRef.current.scrollWidth;
        const clientWidth = testimonialRef.current.clientWidth;
        if (testimonialRef.current.scrollLeft + clientWidth >= scrollWidth) {
          testimonialRef.current.scrollLeft = 0;
        }
      }
    }, 5000);
    
    return () => clearInterval(scrollInterval);
  }, []);

  return (
    <Box sx={{ overflowX: 'hidden' }}>
      {/* Hero Section with Parallelogram Design */}
      <Section sx={{ 
        p: 0,
        position: 'relative',
        overflow: 'hidden',
        minHeight: '100vh',
        background: '#f8fafc'
      }}>
        
        <Grid container sx={{ minHeight: '100vh', position: 'relative', zIndex: 1 }}>
            {/* Left Side - Content with Water Drop Animation */}
            <Grid item xs={12} md={6} sx={{ 
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              p: { xs: 4, md: 6 },
              minHeight: '100%',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Empty background container */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: -1
                }}
              />
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                sx={{ maxWidth: '600px' }}
              >
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    color: colors.primary,
                    fontWeight: 600,
                    mb: 1,
                    textTransform: 'uppercase',
                    letterSpacing: 1
                  }}
                >
                  Water Treatment Technologies
                </Typography>
                
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontWeight: 700,
                    mb: 3,
                    color: colors.text.primary
                  }}
                >
                  Advanced <Box component="span" sx={{ color: colors.primary }}>Water Solutions</Box>
                </Typography>
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: colors.text.secondary,
                    mb: 2,
                    fontSize: '1.1rem',
                    lineHeight: 1.6
                  }}
                >
                  Providing comprehensive water treatment and purification systems for industrial and commercial applications
                </Typography>
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: colors.text.secondary,
                    mb: 2,
                    fontSize: '1rem',
                    lineHeight: 1.6
                  }}
                >
                  Our cutting-edge solutions combine innovative technology with decades of industry expertise to deliver sustainable and efficient water management systems tailored to your specific needs.
                </Typography>
                
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: '8px',
                    background: 'rgba(16, 185, 129, 0.05)',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: 'rgba(16, 185, 129, 0.1)',
                      }}
                    >
                      <Recycling sx={{ color: '#10b981', fontSize: '1.5rem' }} />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#10b981',
                        fontWeight: 600,
                        fontSize: '1.1rem'
                      }}
                    >
                      Environmental Sustainability
                    </Typography>
                  </Box>
                  
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: colors.text.secondary,
                      fontSize: '1rem',
                      lineHeight: 1.6
                    }}
                  >
                    We develop green technologies that minimize ecological impact while maximizing resource efficiency and compliance with global environmental standards.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Recycling sx={{ color: '#10b981', fontSize: '1.2rem' }} />
                      <Typography variant="body2">Eco-Friendly Solutions</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Recycling sx={{ color: '#10b981', fontSize: '1.2rem' }} />
                      <Typography variant="body2">Zero Waste Systems</Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 2, 
                  mb: 3,
                  '& > div': {
                    display: 'flex',
                    alignItems: 'center',
                    mr: 3,
                    mb: 1
                  },
                  '& svg': {
                    color: colors.primary,
                    mr: 1,
                    fontSize: '1.2rem'
                  }
                }}>
                  <Box>
                    <CheckCircleOutline />
                    <Typography variant="body2">ISO 9001 Certified</Typography>
                  </Box>
                  <Box>
                    <CheckCircleOutline />
                    <Typography variant="body2">24/7 Support</Typography>
                  </Box>
                  <Box>
                    <CheckCircleOutline />
                    <Typography variant="body2">Global Service Network</Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2, mb: 5 }}>
                  <Button 
                    variant="contained" 
                    component={RouterLink}
                    to="/solutions"
                    sx={{ 
                      bgcolor: colors.primary,
                      px: 3,
                      py: 1,
                      '&:hover': {
                        bgcolor: colors.secondary
                      }
                    }}
                  >
                    Our Solutions
                  </Button>
                  
                  <Button 
                    variant="outlined" 
                    component={RouterLink}
                    to="/contact"
                    sx={{ 
                      borderColor: colors.primary,
                      color: colors.primary,
                      px: 3,
                      py: 1,
                      '&:hover': {
                        borderColor: colors.secondary,
                        color: colors.secondary
                      }
                    }}
                  >
                    Contact Us
                  </Button>
                </Box>
                
                <Grid container spacing={3}>
                  {stats.slice(0, 3).map((stat, index) => (
                    <Grid item xs={4} key={index}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography 
                          variant="h4" 
                          sx={{ 
                            fontWeight: 700, 
                            color: colors.primary,
                            mb: 0.5
                          }}
                        >
                          <CountUp end={stat.value} duration={2} />{stat.suffix}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: colors.text.secondary,
                            fontWeight: 500
                          }}
                        >
                          {stat.label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </Grid>
            
            {/* Right Side - Slideshow with Parallelogram Design */}
            <Grid item xs={12} md={6} sx={{ position: 'relative', minHeight: '100vh', p: 0 }}>
              <Box sx={{ 
                position: 'relative',
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                clipPath: 'polygon(15% 0, 100% 0, 100% 100%, 0% 100%)',
                boxShadow: '-10px 0 30px rgba(0,0,0,0.1)'
              }}>
                {slideshowImages.map((img, index) => (
                  <Box
                    key={index}
                    component={motion.div}
                    initial={{ opacity: 0 }}
                    animate={{ 
                      opacity: activeSlide === index ? 1 : 0,
                      scale: activeSlide === index ? 1 : 1.1
                    }}
                    transition={{ duration: 0.7 }}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      '& img': {
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      },
                    }}
                  >
                    <img src={img} alt={`Slide ${index + 1}`} />
                  </Box>
                ))}
                
                {/* Slide indicators */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: 1,
                    zIndex: 10,
                  }}
                >
                  {slideshowImages.map((_, index) => (
                    <Box
                      key={index}
                      onClick={() => setActiveSlide(index)}
                      sx={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: activeSlide === index ? 'white' : 'rgba(255,255,255,0.5)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
      </Section>

      {/* Advantages Section */}
      <Section sx={{ 
        py: { xs: 6, md: 8 }, 
        background: 'linear-gradient(135deg, #051e34 0%, #0a3060 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* ... existing advantages section code ... */}
      </Section>

      {/* Video Section */}
      <Section sx={{ 
        py: { xs: 6, md: 8 }, 
        background: '#ffffff', 
        position: 'relative', 
        overflow: 'hidden',
        borderTop: '1px solid #f0f0f0',
        borderBottom: '1px solid #f0f0f0'
      }}>
        {/* ... existing video section code ... */}
      </Section>

      {/* Video and Advantages Section */}
      <Section 
        sx={{ 
          pt: 3, 
          pb: 4, 
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #f1f5f9 0%, #e0f2fe 100%)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'radial-gradient(circle at 20% 30%, rgba(14, 165, 233, 0.03) 0%, transparent 25%), radial-gradient(circle at 80% 70%, rgba(13, 148, 136, 0.03) 0%, transparent 25%)',
            opacity: 1,
            zIndex: 0
          }
        }}
      >
        {/* Decorative Elements */}
        <Box 
          component={motion.div}
          animate={{ 
            y: [0, -15, 0],
            rotate: [0, 5, 0]
          }}
          transition={{ 
            duration: 8, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
          sx={{ 
            position: 'absolute', 
            top: '10%',
            left: '5%',
            width: '200px',
            height: '200px',
            borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
            background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.05), rgba(13, 148, 136, 0.05))',
            filter: 'blur(40px)',
            zIndex: 0
          }} 
        />
        <Box 
          component={motion.div}
          animate={{ 
            y: [0, 20, 0],
            rotate: [0, -8, 0]
          }}
          transition={{ 
            duration: 10, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
          sx={{ 
            position: 'absolute', 
            bottom: '15%',
            right: '8%',
            width: '300px',
            height: '300px',
            borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
            background: 'linear-gradient(135deg, rgba(13, 148, 136, 0.05), rgba(14, 165, 233, 0.05))',
            filter: 'blur(40px)',
            zIndex: 0
          }} 
        />
        
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Box 
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            sx={{ 
              textAlign: 'center', 
              mb: 2,
              position: 'relative'
            }}
          >
            <Box 
              sx={{ 
                display: 'inline-block',
                position: 'relative',
                mb: 1
              }}
            >
              <Typography 
                variant="h6" 
                component="span"
                sx={{ 
                  fontWeight: 600, 
                  color: '#0ea5e9',
                  letterSpacing: '3px',
                  textTransform: 'uppercase',
                  fontSize: '0.95rem'
                }}
              >
                Excellence in Water Management
              </Typography>
            </Box>
            
            <Typography 
              variant="h2" 
              component="h2"
              sx={{ 
                fontWeight: 800, 
                mb: 1,
                fontSize: { xs: '2rem', md: '2.5rem' },
                color: '#00A9CE',
                display: 'inline-block',
                position: 'relative',
                letterSpacing: '1px'
              }}
            >
              INNOVATIVE WATER SOLUTIONS
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                color: colors.text.secondary,
                maxWidth: '800px',
                mx: 'auto',
                fontWeight: 400,
                lineHeight: 1.5,
                fontSize: { xs: '0.9rem', md: '1rem' }
              }}
            >
              Discover how our cutting-edge technologies are revolutionizing water treatment
              with unmatched efficiency and sustainability
            </Typography>
            
            <Box 
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                mt: 2
              }}
            >
              <Box 
                sx={{ 
                  width: '30px', 
                  height: '2px', 
                  background: '#00A9CE',
                  borderRadius: '2px'
                }} 
              />
              <WaterDrop sx={{ color: '#00A9CE', fontSize: '1rem', opacity: 0.8 }} />
              <Box 
                sx={{ 
                  width: '30px', 
                  height: '2px', 
                  background: '#00A9CE',
                  borderRadius: '2px'
                }} 
              />
            </Box>
          </Box>
        </Container>
        <Container maxWidth="xl" sx={{ position: 'relative' }}>
          <Box 
            sx={{ 
              position: 'absolute', 
              width: '300px', 
              height: '300px', 
              borderRadius: '50%', 
              background: 'radial-gradient(circle, rgba(14, 165, 233, 0.1) 0%, rgba(14, 165, 233, 0) 70%)',
              top: '-150px',
              left: '-150px',
              zIndex: 0
            }} 
          />
          <Box 
            sx={{ 
              position: 'absolute', 
              width: '200px', 
              height: '200px', 
              borderRadius: '50%', 
              background: 'radial-gradient(circle, rgba(13, 148, 136, 0.1) 0%, rgba(13, 148, 136, 0) 70%)',
              bottom: '-100px',
              right: '-100px',
              zIndex: 0
            }} 
          />
          <Grid container spacing={2} alignItems="center">
            {/* Left side - Auto-playing video */}
            <Grid item xs={12} md={6}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                sx={{
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '6px',
                    background: 'linear-gradient(90deg, #0ea5e9, #0d9488)',
                    zIndex: 1
                  }
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  {/* Patent Technologies Label */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      backgroundColor: '#0ea5e9',
                      color: 'white',
                      padding: '8px 16px 8px 0',
                      clipPath: 'polygon(0 0, 100% 0, 90% 100%, 0 100%)',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      letterSpacing: '1px',
                      zIndex: 2,
                      display: 'flex',
                      alignItems: 'center',
                      boxShadow: '2px 2px 10px rgba(0, 0, 0, 0.3)',
                      textTransform: 'uppercase'
                    }}
                  >
                    <Box 
                      sx={{ 
                        backgroundColor: '#0d9488', 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 1,
                        mr: 1.5
                      }}
                    >
                      <Science sx={{ fontSize: '1.2rem' }} />
                    </Box>
                    Patent Technologies
                  </Box>
                  
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      component="video"
                      ref={videoRef}
                      src={videoPlaylist[currentVideoIndex]}
                      autoPlay={true} // Ensure autoplay is explicitly enabled
                      muted={isMuted} // Start muted to ensure autoplay works
                      playsInline
                      loop // Add loop to ensure continuous playback
                      onEnded={() => {
                        // When video ends, shuffle to next video or loop if only one
                        if (videoPlaylist.length > 1) {
                          // Get a random index that's different from current
                          let nextIndex;
                          do {
                            nextIndex = Math.floor(Math.random() * videoPlaylist.length);
                          } while (nextIndex === currentVideoIndex && videoPlaylist.length > 1);
                          
                          setCurrentVideoIndex(nextIndex);
                        } else {
                          // If only one video, just replay it
                          if (videoRef.current) {
                            videoRef.current.currentTime = 0;
                            videoRef.current.play();
                          }
                        }
                      }}
                      sx={{
                        width: '100%',
                        display: 'block',
                        borderRadius: '12px',
                      }}
                    />
                    
                    {/* Unmute button */}
                    {isMuted && (
                      <Box
                        onClick={() => setIsMuted(false)}
                        sx={{
                          position: 'absolute',
                          bottom: '20px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          backgroundColor: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          padding: '10px 20px',
                          borderRadius: '30px',
                          fontSize: '0.9rem',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          zIndex: 10,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.8)',
                            transform: 'translateX(-50%) scale(1.05)',
                          }
                        }}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M11 5L6 9H2V15H6L11 19V5Z" fill="white"/>
                          <path d="M14.5 8.5C14.5 8.5 16 10.5 16 12C16 13.5 14.5 15.5 14.5 15.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M17.5 5.5C17.5 5.5 21 8 21 12C21 16 17.5 18.5 17.5 18.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Click to Unmute
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>
            
            {/* Right side - Why Us Advantages */}
            <Grid item xs={12} md={6}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <Box sx={{ mb: 1, textAlign: 'center' }}>
                  <Typography 
                    variant="h2" 
                    component="h2"
                    sx={{ 
                      fontWeight: 800, 
                      mb: 1, 
                      color: '#0f172a',
                      fontSize: { xs: '1.75rem', md: '2rem' },
                      position: 'relative',
                      display: 'inline-block',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: '-8px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '40px',
                        height: '3px',
                        background: '#00A9CE',
                        borderRadius: '2px'
                      }
                    }}
                  >
                    WHY US?
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: colors.text.secondary,
                      mt: 2,
                      mb: 1,
                      fontWeight: 400,
                      fontSize: '0.9rem',
                      maxWidth: '500px',
                      mx: 'auto'
                    }}
                  >
                    Our competitive advantages that set us apart in the water treatment industry
                  </Typography>
                </Box>
                
                <Box sx={{ mt: 2 }}>
                  {/* LESS FOOTPRINT */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      p: 2,
                      borderRadius: '8px',
                      background: 'linear-gradient(90deg, #00BCD4 5%, rgba(0, 188, 212, 0.1) 100%)',
                      position: 'relative',
                      overflow: 'hidden',
                      clipPath: 'polygon(0 0, 95% 0, 100% 100%, 0% 100%)',
                      boxShadow: '0 4px 15px rgba(0, 188, 212, 0.2)'
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        LESS FOOTPRINT
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#334155' }}>
                        Minimum as 0.5 sq.m/cubic meter
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 60,
                        height: 60,
                      }}
                    >
                      <Construction sx={{ fontSize: 40, color: '#00838F' }} />
                    </Box>
                  </Box>

                  {/* HIGHER RECOVERY */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      p: 2,
                      borderRadius: '8px',
                      background: 'linear-gradient(90deg, #8BC34A 5%, rgba(139, 195, 74, 0.1) 100%)',
                      position: 'relative',
                      overflow: 'hidden',
                      clipPath: 'polygon(0 0, 95% 0, 100% 100%, 0% 100%)',
                      boxShadow: '0 4px 15px rgba(139, 195, 74, 0.2)'
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        HIGHER RECOVERY
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#334155' }}>
                        More than 97% Recovery with Membrane Technology
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 60,
                        height: 60,
                      }}
                    >
                      <Recycling sx={{ fontSize: 40, color: '#558B2F' }} />
                    </Box>
                  </Box>

                  {/* AFFORDABLE CAPEX & OPEX */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      p: 2,
                      borderRadius: '8px',
                      background: 'linear-gradient(90deg, #03A9F4 5%, rgba(3, 169, 244, 0.1) 100%)',
                      position: 'relative',
                      overflow: 'hidden',
                      clipPath: 'polygon(0 0, 95% 0, 100% 100%, 0% 100%)',
                      boxShadow: '0 4px 15px rgba(3, 169, 244, 0.2)'
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        AFFORDABLE CAPEX & OPEX
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#334155' }}>
                        Lowest Power consumption - 2.5 kW/cubic meter upto RO
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 60,
                        height: 60,
                      }}
                    >
                      <Box component="span" sx={{ fontSize: 40, color: '#0277BD' }}>₹</Box>
                    </Box>
                  </Box>

                  {/* AUTOMATION */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      p: 2,
                      borderRadius: '8px',
                      background: 'linear-gradient(90deg, #9C27B0 5%, rgba(156, 39, 176, 0.1) 100%)',
                      position: 'relative',
                      overflow: 'hidden',
                      clipPath: 'polygon(0 0, 95% 0, 100% 100%, 0% 100%)',
                      boxShadow: '0 4px 15px rgba(156, 39, 176, 0.2)'
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        AUTOMATION
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#334155' }}>
                        PLC & SCADA Operated Plant with Minimum Manpower
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 60,
                        height: 60,
                      }}
                    >
                      <Settings sx={{ fontSize: 40, color: '#6A1B9A' }} />
                    </Box>
                  </Box>

                  {/* EQUIPMENT MANUFACTURERS */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      p: 2,
                      borderRadius: '8px',
                      background: 'linear-gradient(90deg, #F44336 5%, rgba(244, 67, 54, 0.1) 100%)',
                      position: 'relative',
                      overflow: 'hidden',
                      clipPath: 'polygon(0 0, 95% 0, 100% 100%, 0% 100%)',
                      boxShadow: '0 4px 15px rgba(244, 67, 54, 0.2)'
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        EQUIPMENT MANUFACTURERS
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#334155' }}>
                        World-Class in-house Manufacturing Facility
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 60,
                        height: 60,
                      }}
                    >
                      <Business sx={{ fontSize: 40, color: '#C62828' }} />
                    </Box>
                  </Box>

                  {/* ADVANCED DATA MONITORING */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      p: 2,
                      borderRadius: '8px',
                      background: 'linear-gradient(90deg, #4CAF50 5%, rgba(76, 175, 80, 0.1) 100%)',
                      position: 'relative',
                      overflow: 'hidden',
                      clipPath: 'polygon(0 0, 95% 0, 100% 100%, 0% 100%)',
                      boxShadow: '0 4px 15px rgba(76, 175, 80, 0.2)'
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        ADVANCED DATA MONITORING
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#334155' }}>
                        Cloud Storage and Mobile App Access for Plant Operating Data
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 60,
                        height: 60,
                      }}
                    >
                      <PhoneAndroidIcon sx={{ fontSize: 40, color: '#2E7D32' }} />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Section>

      {/* Industries Section */}
      <Section sx={{ 
        py: { xs: 8, md: 10 }, 
        background: '#0a192f',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Industries content removed as requested */}
      </Section>

      {/* Testimonials Section */}
      <Section sx={{ 
        py: { xs: 8, md: 10 }, 
        background: '#f8fafc',
        position: 'relative',
        overflow: 'hidden',
        borderTop: '1px solid #e2e8f0',
        borderBottom: '1px solid #e2e8f0'
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(14, 165, 233, 0.1)',
                  borderRadius: '4px',
                  px: 2,
                  py: 0.75,
                  mb: 2
                }}
              >
                <Typography 
                  variant="overline" 
                  sx={{ 
                    color: colors.primary,
                    letterSpacing: 1,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase'
                  }}
                >
                  Testimonials
                </Typography>
              </Box>
              
              <Typography 
                variant="h2" 
                sx={{ 
                  color: colors.text.primary, 
                  mb: 2, 
                  fontWeight: 700,
                  fontSize: { xs: '2rem', md: '2.5rem' }
                }}
              >
                What Our Clients Say
              </Typography>
              
              <Typography 
                variant="body1" 
                sx={{ 
                  color: colors.text.secondary, 
                  maxWidth: '700px',
                  mx: 'auto',
                  mb: 4,
                  fontWeight: 400,
                  lineHeight: 1.6
                }}
              >
                Our clients trust us to deliver reliable and efficient water treatment solutions that meet their specific industry requirements
              </Typography>
            </motion.div>
          </Box>
          
          <Box sx={{ position: 'relative', overflow: 'hidden' }}>
            <Box
              ref={testimonialRef}
              sx={{
                display: 'flex',
                gap: 3,
                pb: 2,
                px: { xs: 2, md: 4 },
                overflowX: 'auto',
                scrollBehavior: 'smooth',
                '&::-webkit-scrollbar': {
                  height: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f5f9',
                  borderRadius: '10px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#cbd5e1',
                  borderRadius: '10px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#94a3b8',
                }
              }}
            >
              {/* Testimonial 1 */}
              <Box
                sx={{
                  minWidth: { xs: '85%', sm: '60%', md: '350px' },
                  p: 3,
                  borderRadius: '12px',
                  background: 'white',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  borderLeft: `3px solid ${colors.primary}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', mb: 2 }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Box key={star} component="span" sx={{ color: colors.warning, mr: 0.5, fontSize: '0.9rem' }}>
                      ★
                    </Box>
                  ))}
                </Box>
                
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.95rem',
                    color: colors.text.secondary,
                    lineHeight: 1.6,
                    mb: 3,
                    fontStyle: 'italic',
                    flexGrow: 1
                  }}
                >
                  "WTT International's water treatment solutions have significantly improved our manufacturing efficiency while reducing our environmental footprint."
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: '#e0f2fe',
                      color: colors.primary,
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      mr: 1.5
                    }}
                  >
                    RS
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colors.text.primary, fontSize: '0.875rem' }}>
                      Rajesh Sharma
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.text.secondary, fontSize: '0.75rem' }}>
                      Textile Industries Ltd.
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Testimonial 2 */}
              <Box
                sx={{
                  minWidth: { xs: '85%', sm: '60%', md: '350px' },
                  p: 3,
                  borderRadius: '12px',
                  background: 'white',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  borderLeft: `3px solid ${colors.secondary}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', mb: 2 }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Box key={star} component="span" sx={{ color: colors.warning, mr: 0.5, fontSize: '0.9rem' }}>
                      ★
                    </Box>
                  ))}
                </Box>
                
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.95rem',
                    color: colors.text.secondary,
                    lineHeight: 1.6,
                    mb: 3,
                    fontStyle: 'italic',
                    flexGrow: 1
                  }}
                >
                  "The Zero Liquid Discharge system implemented by WTT International has helped us achieve our sustainability goals while reducing operational costs by 30%."
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: '#ccfbf1',
                      color: colors.secondary,
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      mr: 1.5
                    }}
                  >
                    VP
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colors.text.primary, fontSize: '0.875rem' }}>
                      Vikram Patel
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.text.secondary, fontSize: '0.75rem' }}>
                      Pharma Solutions
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Testimonial 3 */}
              <Box
                sx={{
                  minWidth: { xs: '85%', sm: '60%', md: '350px' },
                  p: 3,
                  borderRadius: '12px',
                  background: 'white',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  borderLeft: `3px solid ${colors.success}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', mb: 2 }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Box key={star} component="span" sx={{ color: colors.warning, mr: 0.5, fontSize: '0.9rem' }}>
                      ★
                    </Box>
                  ))}
                </Box>
                
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.95rem',
                    color: colors.text.secondary,
                    lineHeight: 1.6,
                    mb: 3,
                    fontStyle: 'italic',
                    flexGrow: 1
                  }}
                >
                  "We've partnered with WTT International for over 5 years, and their water treatment expertise has been crucial to our dairy processing operations."
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: '#d1fae5',
                      color: colors.success,
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      mr: 1.5
                    }}
                  >
                    AM
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colors.text.primary, fontSize: '0.875rem' }}>
                      Anita Menon
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.text.secondary, fontSize: '0.75rem' }}>
                      Dairy Products Inc.
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Testimonial 4 */}
              <Box
                sx={{
                  minWidth: { xs: '85%', sm: '60%', md: '350px' },
                  p: 3,
                  borderRadius: '12px',
                  background: 'white',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  borderLeft: `3px solid ${colors.info}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', mb: 2 }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Box key={star} component="span" sx={{ color: colors.warning, mr: 0.5, fontSize: '0.9rem' }}>
                      ★
                    </Box>
                  ))}
                </Box>
                
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.95rem',
                    color: colors.text.secondary,
                    lineHeight: 1.6,
                    mb: 3,
                    fontStyle: 'italic',
                    flexGrow: 1
                  }}
                >
                  "Their innovative approach to water recycling has transformed our paper manufacturing process, resulting in significant cost savings and environmental compliance."
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: '#dbeafe',
                      color: colors.info,
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      mr: 1.5
                    }}
                  >
                    SK
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colors.text.primary, fontSize: '0.875rem' }}>
                      Sunil Kumar
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.text.secondary, fontSize: '0.75rem' }}>
                      Paper Mills Ltd.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Section>
    </Box>
  );
}

